import { Button } from '@/components/common/button'
import { Card } from '@/components/common/card'
import { NetworkImage } from '@/components/common/networkImage'
import { RoundImage } from '@/components/common/roundImage'
import { TokenImage } from '@/components/common/tokenImage'
import { ExternalLinkIcon } from '@/components/icons/externalLink'
import { shortenAddress } from '@/utils/address/shortenAddress'
import { getExplorerLink, getExplorerLinkFromAssetCaip } from '@/utils/explorer'
import type { FixedTermMarket } from '@/utils/internalAppSdk'
import { getNetworkName } from '@/utils/network/getNetworkName'
import { getProtocolDisplayName } from '@/utils/protocol'
import { SECOND_IN_MILLISECONDS } from '@vaultsfyi/common/constants'
import { formatDate, getProtocolImage } from '@vaultsfyi/common/utils'
import type { ReactNode } from 'react'
import type { Address } from 'viem'

interface Props {
  market: FixedTermMarket
}

interface DetailRowProps {
  label: string
  children: ReactNode
}

export const FixedTermMarketDetails = ({ market }: Props) => {
  const marketExplorerLink = getExplorerLink(market.network.name)
  const maturityDate = formatDate({
    timestamp: market.expiry * SECOND_IN_MILLISECONDS,
    options: { day: 'numeric', month: 'long', year: 'numeric', timeZone: 'UTC' },
  })

  return (
    <Card as="section" className="flex flex-col gap-2 p-4 md:p-5">
      <h2 className="font-medium text-base text-heading-foreground">Details</h2>
      <dl>
        <DetailRow label="Protocol">
          <ProtocolValue protocol={market.protocol} />
        </DetailRow>
        <DetailRow label="Underlying protocol">
          <ProtocolValue protocol={market.underlyingProtocol} />
        </DetailRow>
        <DetailRow label="Network">
          {marketExplorerLink ? (
            <Button variant="default" size="default" className="gap-1.5 underline" asChild>
              <a href={marketExplorerLink} target="_blank" rel="noreferrer">
                <NetworkImage network={market.network.name} className="size-5" />
                <span className="capitalize">{getNetworkName(market.network.name)}</span>
                <ExternalLinkIcon size={12} />
              </a>
            </Button>
          ) : (
            <span className="flex items-center justify-end gap-1.5">
              <NetworkImage network={market.network.name} className="size-5" />
              <span className="capitalize">{getNetworkName(market.network.name)}</span>
            </span>
          )}
        </DetailRow>
        <DetailRow label="Underlying asset">
          <TokenValue token={market.underlyingAsset} />
        </DetailRow>
        <DetailRow label="Accounting asset">
          <TokenValue token={market.accountingAsset} />
        </DetailRow>
        <DetailRow label="Maturity date">{maturityDate}</DetailRow>
        <DetailRow label="PT token">
          <TokenValue token={market.pt} />
        </DetailRow>
      </dl>
    </Card>
  )
}

const DetailRow = ({ label, children }: DetailRowProps) => (
  <div className="flex min-h-11 items-center border-b last:border-b-0">
    <dt className="min-w-0 flex-1 py-2 pr-2 text-sm">{label}</dt>
    <dd className="min-w-0 flex-1 py-2 pl-2 text-right text-heading-foreground text-sm">{children}</dd>
  </div>
)

interface ProtocolValueProps {
  protocol: FixedTermMarket['protocol']
}

const ProtocolValue = ({ protocol }: ProtocolValueProps) => {
  const label = getProtocolDisplayName(protocol)
  const content = (
    <>
      <RoundImage
        src={protocol.protocolLogo ?? getProtocolImage(protocol.name)}
        fallback={getProtocolImage('fallback')}
        alt=""
        className="size-5 p-0.5"
      />
      <span>{label}</span>
    </>
  )

  if (!protocol.protocolUrl) {
    return <span className="flex items-center justify-end gap-1.5">{content}</span>
  }

  return (
    <Button variant="default" size="default" className="gap-1.5 underline" asChild>
      <a href={protocol.protocolUrl} target="_blank" rel="noreferrer">
        {content}
        <ExternalLinkIcon size={12} />
      </a>
    </Button>
  )
}

interface TokenValueProps {
  token: FixedTermMarket['pt']
}

const TokenValue = ({ token }: TokenValueProps) => {
  const value = token.symbol ?? token.name ?? shortenAddress(token.address)
  const explorerLink = getExplorerLinkFromAssetCaip(token.assetCaip)

  return (
    <AddressValue
      address={token.address}
      value={value}
      explorerLink={explorerLink}
      leadingContent={token.symbol ? <TokenImage symbol={token.symbol} className="size-5" /> : undefined}
      valueClassName={token.symbol || token.name ? undefined : 'tabular-nums'}
    />
  )
}

interface AddressValueProps {
  address: Address
  value: string
  explorerLink: string | null
  leadingContent?: ReactNode
  valueClassName?: string
}

const AddressValue = ({ address, value, explorerLink, leadingContent, valueClassName }: AddressValueProps) => (
  <span className="flex items-center justify-end gap-1.5">
    {leadingContent}
    {explorerLink ? (
      <Button variant="default" size="default" className="gap-1 underline" asChild>
        <a href={`${explorerLink}/address/${address}`} target="_blank" rel="noreferrer">
          <span className={valueClassName}>{value}</span>
          <ExternalLinkIcon size={12} />
        </a>
      </Button>
    ) : (
      <span className={valueClassName}>{value}</span>
    )}
  </span>
)
